import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { tap, catchError, take, takeUntil } from 'rxjs/operators';
import { NotificationService } from '../services/notification.service';
import { InvoiceService } from '../services/invoice.service';
import { EMPTY, Subject } from 'rxjs';
import { InvoiceContent } from '../invoice/models/invoiceContent.model';
import { Signature } from './models/signature.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SignatureInputComponent } from '../signature-input/signature-input.component';
import { UntypedFormGroup } from '@angular/forms';
import { PathFinderService } from '../services/path-finder.service';
import { LoadingService } from '../services/loading.service';
import { InvoiceSignatureInputComponent } from '../signature-input/invoice-signature-input.component';
import { EventsService } from '../services/events.service';
import { DashboardService } from '../services/dashboard.service';
import { BuyerStatsSummary } from '../dashboard/model/buyer-stats-summary.model';

@Component({
  selector: 'app-invoice',
  templateUrl: './invoice.component.html'
})
export class InvoiceComponent implements OnInit, OnChanges, OnDestroy {
  @Input() quoteStateId: number;
  @Input() group: UntypedFormGroup;
  @Input() canLoadContent: boolean;

  loading: boolean;
  today: Date;
  isDataReady: boolean;
  purchaseDetails: InvoiceContent;
  customerSignature: Signature;
  buyerSignature: Signature;
  buyerStatsSummary: BuyerStatsSummary;
  destroy$ = new Subject();

  constructor(private modalService: NgbModal,
    private notifications: NotificationService,
    private invoiceService: InvoiceService,
    private loadingService: LoadingService,
    private pathFinder: PathFinderService,
    private eventService: EventsService,
    private dashboardService: DashboardService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.canLoadContent.firstChange && changes.canLoadContent.currentValue === undefined) || changes.canLoadContent.currentValue) {
      this.loadData(true);
    }
  }

  loadData(value: boolean) {
    this.loading = true;
    this.loadingService.loading.emit(this.loading);
    this.isDataReady = value;
    this.retrieveInvoiceData();
  }

  ngOnInit() {
    this.today = new Date();
    this.loading = true;
    this.loadingService.loading.emit(this.loading);
    this.pathFinder.registerTransitionEvent((onComplete => {
      const signatures = [this.customerSignature, this.buyerSignature];
      this.invoiceService.generateInvoice$(this.quoteStateId, signatures).pipe(
        tap(() => {
          this.showConversionBudgetTracking();
          onComplete(true);
        }),
        catchError(err => {
          this.notifications.dangerToast(`Failed to generate invoice for purchaseId: ${this.quoteStateId}`, err);
          onComplete(false);
          return EMPTY;
        }),
        take(1)
      ).subscribe();
    }));

    this.getBuyerStatsSummary();

    this.eventService.answersSaved.pipe(takeUntil(this.destroy$), take(1)).subscribe(value => {
      this.loadData(value);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  retrieveInvoiceData() {
    if (this.isDataReady) {
      this.invoiceService.getInvoiceDetails$(this.quoteStateId).pipe(
        tap(result => {
          this.purchaseDetails = result;
          this.loading = false;
          this.loadingService.loading.emit(this.loading);
        }),
        catchError(err => {
          this.loading = false;
          this.loadingService.loading.emit(this.loading);
          this.notifications.dangerToast('Failed to retrieve invoice details', err);
          return EMPTY;
        }),
        take(1)
      ).subscribe();
    }
  }

  signatureAdded() {
    if (this.isInvoiceComplete()) {
      this.group.controls.value.setValue(true);
    }
  }

  isInvoiceComplete() {
    return this.buyerSignature && this.customerSignature;
  }

  customerSign() {
    const modalRef = this.modalService.open(InvoiceSignatureInputComponent, {
      keyboard: false,
      backdrop: 'static',
      centered: true,
      size: 'xl',
      windowClass: 'signatureWindow'
    });
    modalRef.componentInstance.quoteStateId = this.quoteStateId;
    modalRef.componentInstance.purchaseDetails = this.purchaseDetails.purchaseDetails;
    modalRef.result.then(result => {
      this.customerSignature = result;
      this.signatureAdded();
    }, () => { });
  }

  buyerSign() {
    const modalRef = this.modalService.open(SignatureInputComponent, {
      keyboard: false,
      backdrop: 'static',
      centered: true,
      size: 'xl',
      windowClass: 'signatureWindow'
    });
    modalRef.componentInstance.isWbacSignature = true;
    modalRef.componentInstance.quoteStateId = this.quoteStateId;
    modalRef.componentInstance.disclaimer = 'Authorised for and on behalf of WE BUY ANY CAR Limited';
    modalRef.result.then(result => {
      this.buyerSignature = result;
      this.signatureAdded();
    }, () => { });
  }

  getBuyerStatsSummary() {
    return this.dashboardService
      .getConversionBudget$()
      .pipe(
        tap((result) => {
          this.buyerStatsSummary = result;
        }),
        catchError(() => {
          return EMPTY;
        }),
        take(1)
      )
      .subscribe();
    }

    showConversionBudgetTracking() {
      if (this.buyerStatsSummary) {
        let message: string;
        let indicator: string;

        // Need to add this purchase and appointment to retrieved stats
        const purchases = this.buyerStatsSummary.curPurchases + 1;
        const appointments = this.buyerStatsSummary.curAppointments + 1;
        const gap = purchases - (appointments * this.buyerStatsSummary.conversionTarget);

        if (gap >= 0){
          indicator = 'G';
          message = 'You\'re currently <b>' + this.formatGap(gap) + ' ' + this.pluralize('vehicle', Math.abs(gap)) + ' ahead of target.</b> Keep up the great work.';
        }
        else if (gap >= -1.5) {
          indicator = 'A';
          message = 'You\'re <b>' + this.formatGap(gap) + ' ' + this.pluralize('vehicle', Math.abs(gap)) + ' behind target.</b> Just a few more purchases will get you back on track.';
        }
        else {
          indicator = 'R';
          message = 'You\'re <b>' + this.formatGap(gap) + ' ' + this.pluralize('vehicle', Math.abs(gap)) + ' behind target.</b> Focus on the next purchase to start closing the gap.';
        }

        const displayTime = this.pathFinder.getPurchaseSettingAsNumber('ConversionBudgetDisplayTime');

        this.notifications.infoDescriptiveToast(message, undefined, indicator, displayTime);
      }
    }

    formatGap(gap: number): string {
      let formattedGap = Math.abs(gap).toFixed(1);
      if (formattedGap.endsWith('.0')) {
        formattedGap = formattedGap.slice(0, -2);
      }
      return formattedGap;
    }

    pluralize(word: string, count: number): string {
      return count === 1 ? word : word + 's';
    }
}
